import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Accordion, AccordionDetails, AccordionSummary, Typography, Tooltip } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Button from '@material-ui/core/Button';
import { toast } from 'react-toastify';
import RenderComponentBasedOnBlock from './RenderComponentBasedOnBlock';
import CreateIcon from '@material-ui/icons/Create';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import WorkFlowModelViewer from './workFlowModal';
import CommentViewer from './Comments';
import Avatar from '@material-ui/core/Avatar';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  /* ===========================
     ACCORDION
  =========================== */
  accordionRoot: {
    '&::before': { display: 'none' },
    borderRadius: '4px 4px 0 0',
    border: '1px solid #EEE',
    // backgroundColor: '#FAFAFA',
    boxShadow: 'none',
    '&.Mui-expanded': { margin: 0 },

    /* lg and below: slightly softer border presence */
    [theme.breakpoints.down('lg')]: {
      borderRadius: '4px',
    },
    [theme.breakpoints.down('md')]: {
      borderRadius: '3px',
    },
    [theme.breakpoints.down('sm')]: {
      borderRadius: '3px',
    },
  },

  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '70vh', // Set a maximum height
    overflowY: 'auto', // Enable vertical scrolling
    // padding: '16px 24px',
    '&::-webkit-scrollbar': {
      display: 'none !important',
    },
    scrollbarWidth: 'none !important',
    msOverflowStyle: 'none !important',

    [theme.breakpoints.down('lg')]: {
      maxHeight: '65vh',
      padding: '14px 20px'
    },
    [theme.breakpoints.down('md')]: {
      maxHeight: '60vh',
      padding: '12px 16px'
    },
    [theme.breakpoints.down('sm')]: {
      maxHeight: '55vh',
      padding: '10px 12px'
    },
    [theme.breakpoints.down('xs')]: {
      maxHeight: '50vh',
      padding: '8px 10px'
    },
  },

  expandedSummary: {
    /* xl baseline */
    '&.MuiAccordionSummary-root': { maxHeight: 97, minHeight: 97 },
    '&.Mui-expanded': { maxHeight: 97, minHeight: 97 },
    '& .MuiAccordionSummary-expandIcon': { marginRight: '30px !important' },
    '& .MuiAccordionSummary-expandIcon.Mui-expanded': { marginRight: '30px !important' },
    '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },

    /* lg */
    [theme.breakpoints.down('lg')]: {
      '&.MuiAccordionSummary-root': { maxHeight: 92, minHeight: 92 },
      '&.Mui-expanded': { maxHeight: 92, minHeight: 92 },
      '& .MuiAccordionSummary-expandIcon, & .MuiAccordionSummary-expandIcon.Mui-expanded': {
        marginRight: '24px !important',
      },
    },

    /* md */
    [theme.breakpoints.down('md')]: {
      '&.MuiAccordionSummary-root': { maxHeight: 86, minHeight: 86 },
      '&.Mui-expanded': { maxHeight: 86, minHeight: 86 },
      '& .MuiAccordionSummary-expandIcon, & .MuiAccordionSummary-expandIcon.Mui-expanded': {
        marginRight: '20px !important',
      },
    },

    /* sm */
    [theme.breakpoints.down('sm')]: {
      '&.MuiAccordionSummary-root': { maxHeight: 80, minHeight: 80 },
      '&.Mui-expanded': { maxHeight: 80, minHeight: 80 },
      '& .MuiAccordionSummary-expandIcon, & .MuiAccordionSummary-expandIcon.Mui-expanded': {
        marginRight: '16px !important',
      },
    },

    /* xs */
    [theme.breakpoints.down('xs')]: {
      '&.MuiAccordionSummary-root': { maxHeight: 74, minHeight: 74 },
      '&.Mui-expanded': { maxHeight: 74, minHeight: 74 },
      '& .MuiAccordionSummary-expandIcon, & .MuiAccordionSummary-expandIcon.Mui-expanded': {
        marginRight: '12px !important',
      },
    },
  },

  /* ===========================
     SUMMARY HEADER LAYOUT
  =========================== */
  summaryContainer: {
    display: 'flex',
    gap: 12,

    [theme.breakpoints.down('lg')]: { gap: 10 },
    [theme.breakpoints.down('md')]: { gap: 8 },
    [theme.breakpoints.down('sm')]: { gap: 8, flexWrap: 'wrap' },
    [theme.breakpoints.down('xs')]: { gap: 6, flexDirection: 'column' },
  },
  summaryLeft: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    /* reserve space for icon */

  },
  summaryRight: {
    display: 'flex',
    gap: 4,
    flexDirection: 'column',
    [theme.breakpoints.down('lg')]: { gap: 4 },
    [theme.breakpoints.down('md')]: { gap: 3 },
    [theme.breakpoints.down('sm')]: { gap: 3 },
    [theme.breakpoints.down('xs')]: { gap: 2 },
  },

  /* ===========================
     TYPOGRAPHY
  =========================== */
  title: {
    fontSize: 18,
    fontWeight: 400,
    fontStyle: 'normal',
    color: 'rgba(33, 33, 33, 1)',
    [theme.breakpoints.down('lg')]: { fontSize: 17 },
    [theme.breakpoints.down('md')]: { fontSize: 16 },
    [theme.breakpoints.down('sm')]: { fontSize: 15 },
    [theme.breakpoints.down('xs')]: { fontSize: 14 },
  },
  subtitle: {
    fontSize: 14,
    fontWeight: 400,
    fontStyle: 'normal',
    color: 'rgba(97, 97, 97, 1)',
    [theme.breakpoints.down('lg')]: { fontSize: 13.5 },
    [theme.breakpoints.down('md')]: { fontSize: 13 },
    [theme.breakpoints.down('sm')]: { fontSize: 12.5 },
    [theme.breakpoints.down('xs')]: { fontSize: 12 },
  },

  label: {
    fontSize: 14,
    fontWeight: 400,
    color: 'rgba(97, 97, 97, 1)',
    [theme.breakpoints.down('lg')]: { fontSize: 13.5 },
    [theme.breakpoints.down('md')]: { fontSize: 13 },
    [theme.breakpoints.down('sm')]: { fontSize: 12.5 },
    [theme.breakpoints.down('xs')]: { fontSize: 12 },
  },
  value: {
    fontSize: 14,
    fontWeight: 400,
    color: 'rgba(33, 33, 33, 1)',
    [theme.breakpoints.down('lg')]: { fontSize: 13.5 },
    [theme.breakpoints.down('md')]: { fontSize: 13 },
    [theme.breakpoints.down('sm')]: { fontSize: 12.5 },
    [theme.breakpoints.down('xs')]: { fontSize: 12 },
  },
  linkSm: {
    fontSize: 12,
    fontWeight: 400,
    color: 'rgba(19, 126, 249, 1)',
    [theme.breakpoints.down('md')]: { fontSize: 11.5 },
    [theme.breakpoints.down('sm')]: { fontSize: 11 },
    [theme.breakpoints.down('xs')]: { fontSize: 10.5 },
  },

  /* ===========================
     DETAILS PADDING
  =========================== */
  detailsWrapper: {
    // paddingLeft: 60,
    [theme.breakpoints.down('lg')]: { paddingLeft: 48 },
    [theme.breakpoints.down('md')]: { paddingLeft: 32 },
    [theme.breakpoints.down('sm')]: { paddingLeft: 20 },
    [theme.breakpoints.down('xs')]: { paddingLeft: 12 },
  },

  /* ===========================
     FLEX HELPERS (GAPS/WRAP)
  =========================== */
  flexRowStart32: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    gap: 32,
    [theme.breakpoints.down('lg')]: { gap: 24 },
    [theme.breakpoints.down('md')]: { gap: 16, flexWrap: 'wrap' },
    [theme.breakpoints.down('sm')]: { gap: 12, flexWrap: 'wrap' },
    [theme.breakpoints.down('xs')]: { gap: 10, flexDirection: 'column', alignItems: 'flex-start' },
  },
  flexRowStart8: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    gap: 8,
    [theme.breakpoints.down('md')]: { gap: 8 },
    [theme.breakpoints.down('sm')]: { gap: 6 },
    [theme.breakpoints.down('xs')]: { gap: 6 },
  },
  flexRowEnd12: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    gap: 12,
    [theme.breakpoints.down('md')]: { gap: 10 },
    [theme.breakpoints.down('sm')]: { justifyContent: 'center', gap: 8, flexWrap: 'wrap' },
    [theme.breakpoints.down('xs')]: { gap: 8 },
  },
  flexRowStart4Clickable: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: { gap: 4 },
  },

  /* ===========================
     BUTTONS
  =========================== */
  btnReject: {
    height: 32,
    padding: '12px',
    backgroundColor: '#FF4747',
    borderRadius: 4,
    color: '#fff',
    [theme.breakpoints.down('lg')]: { height: 30, padding: '10px' },
    [theme.breakpoints.down('md')]: { height: 28, padding: '10px' },
    [theme.breakpoints.down('sm')]: { height: 26, padding: '8px' },
    [theme.breakpoints.down('xs')]: { height: 24, padding: '8px' },
  },
  btnAccept: {
    height: 32,
    padding: '12px',
    backgroundColor: 'rgba(56, 168, 129, 1)',
    borderRadius: 4,
    color: '#fff',
    [theme.breakpoints.down('lg')]: { height: 30, padding: '10px' },
    [theme.breakpoints.down('md')]: { height: 28, padding: '10px' },
    [theme.breakpoints.down('sm')]: { height: 26, padding: '8px' },
    [theme.breakpoints.down('xs')]: { height: 24, padding: '8px' },
  },
  btnSave: {
    height: 32,
    padding: '6px 16px',
    color: '#fff',
    borderRadius: 4,
    backgroundColor: 'rgba(2, 136, 209, 1)',
    [theme.breakpoints.down('lg')]: { height: 30, padding: '6px 14px' },
    [theme.breakpoints.down('md')]: { height: 28, padding: '5px 12px' },
    [theme.breakpoints.down('sm')]: { height: 26, padding: '5px 10px' },
    [theme.breakpoints.down('xs')]: { height: 24, padding: '4px 8px' },
    '&:hover': {
      backgroundColor: 'rgba(2, 136, 209, 1)',
      borderColor: '#3F51B5',
      boxShadow: 'none',
    },
  },
  btnMove: {
    height: 32,
    padding: '7px 11px',
    color: '#fff',
    borderRadius: 4,
    backgroundColor: 'rgba(2, 136, 209, 1)',
    [theme.breakpoints.down('lg')]: { height: 30, padding: '6px 10px' },
    [theme.breakpoints.down('md')]: { height: 28, padding: '6px 10px' },
    [theme.breakpoints.down('sm')]: { height: 26, padding: '5px 9px' },
    [theme.breakpoints.down('xs')]: { height: 24, padding: '4px 8px' },
    '&:hover': {
      backgroundColor: 'rgba(2, 136, 209, 1)',
      borderColor: '#3F51B5',
      boxShadow: 'none',
    },
  },
  btnMoveDisabled: {
    backgroundColor: 'rgba(2, 136, 209, 0.5)',
  },

  /* ===========================
     CARD / BLOCK WRAPPER
  =========================== */
  blockWrapper: {
    backgroundColor: '#fff',
    marginTop: 16,
    marginBottom: 16,
    padding: 16,
    border: '1px solid #E9EBF5',
    borderRadius: 6,
    [theme.breakpoints.down('lg')]: { padding: 14, marginTop: 14, marginBottom: 14 },
    [theme.breakpoints.down('md')]: { padding: 12, marginTop: 12, marginBottom: 12 },
    [theme.breakpoints.down('sm')]: { padding: 10, marginTop: 10, marginBottom: 10 },
    [theme.breakpoints.down('xs')]: { padding: 8, marginTop: 8, marginBottom: 8 },
  },

  /* ===========================
     ICONS / IMAGES
  =========================== */

  commentAvatar: {
    height: 24,
    width: 24,
    fontSize: 11,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'normal',
    backgroundColor: '#5398E6',
    color: '#fff',
  },

  greenIcon: {
    color: 'rgba(48, 164, 97, 1)',
    height: 24,
    width: 24,
    [theme.breakpoints.down('lg')]: { height: 24, width: 24 },
    [theme.breakpoints.down('md')]: { height: 22, width: 22 },
    [theme.breakpoints.down('sm')]: { height: 20, width: 20 },
    [theme.breakpoints.down('xs')]: { height: 18, width: 18 },
  },
  expandIcon: {
    color: 'rgba(33, 33, 33, 1)',
    height: 20,
    width: 20,
    [theme.breakpoints.down('md')]: { height: 18, width: 18 },
    [theme.breakpoints.down('sm')]: { height: 18, width: 18 },
    [theme.breakpoints.down('xs')]: { height: 16, width: 16 },
  },
  calendarIcon: {
    height: 18,
    width: 18,
    [theme.breakpoints.down('md')]: { height: 16, width: 16 },
    [theme.breakpoints.down('sm')]: { height: 16, width: 16 },
    [theme.breakpoints.down('xs')]: { height: 14, width: 14 },
  },
  headerIcon: {
    height: 48,
    width: 48,
    // [theme.breakpoints.down('lg')]: { height: 44, width: 44 },
    // [theme.breakpoints.down('md')]: { height: 40, width: 40 },
    // [theme.breakpoints.down('sm')]: { height: 36, width: 36 },
    // [theme.breakpoints.down('xs')]: { height: 32, width: 32 },
  },
}));

const StepLevelBlocksViewer = (props) => {
  const { blocks, accordionSupport, blockState, onRejectObligation, onAcceptObligation, isLinked, enableWorkflow } = props;

  const [modalInfo, setModalInfo] = useState({ enable: false });
  const [allTasksCompleted, setAllTasksCompleted] = useState(false);
  const [currentButtonClicked, setCurrentButtonClicked] = useState('');
  const [firstStepsMembersDropdown, setFirstStepsMembersDropdown] = useState([]);
  const [defaultOptionsDropdown, setDefaultOptionsDropdown] = useState([]);
  const [selectedBlockName, setSelectedBlockName] = useState(null);

  const enableRejectButton = blockState?.actions?.length ? true : false

  const classes = useStyles();

  useEffect(() => {
    if (props.formData?.document[props.data.chartUniqueId + '_execute_obligation_Workflow']) {
      setModalInfo({ enable: false });
      const executeWorkFlow = props.formData?.document[props.data.chartUniqueId + '_execute_obligation_Workflow'];
      const message = executeWorkFlow?.message || 'Workflow executed successfully';
      showConfirmationDialogORToast('success', message);
    } else {
      // keep quiet in prod; console left from original
      // console.log("Error in Update Identify Step");
    }
  }, [props.formData?.document[props.data.chartUniqueId + '_execute_obligation_Workflow']]);

  useEffect(() => {
    if (props.formData?.document[props.data.chartUniqueId + '_completed_obligation']) {
      showConfirmationDialogORToast('completed', 'Workflow Completed successfully');
    }
  }, [props.formData?.document[props.data.chartUniqueId + '_completed_obligation']]);

  useEffect(() => {
    if (props.formData?.document[props.data.chartUniqueId + '_execute_obligation_End_Workflow']) {
      setModalInfo({ enable: false });
      props.completedWorkFlowObligations();
    }
  }, [props.formData?.document[props.data.chartUniqueId + '_execute_obligation_End_Workflow']]);

  useEffect(() => {
    if (props.formData?.document?.[props.data.chartUniqueId + '_obligation_executeWorkflow_reject']) {
      const rejectWorkFlow = props.formData?.document[props.data.chartUniqueId + '_obligation_executeWorkflow_reject'];
      const message = rejectWorkFlow?.message || 'Workflow executed successfully';
      showConfirmationDialogORToast('rejected', message);
    }
  }, [props.formData?.document?.[props.data.chartUniqueId + '_obligation_executeWorkflow_reject']]);

  const getNextRespondents = () => {
    let data = [];
    (props.formData?.document[props.data.chartUniqueId + '_Obligations_Next_Respondent'] || []).map((i) => {
      if (i.id || i._id) {
        data.push({ value: i.id || i._id, label: i.name });
      }
      return null;
    });
    return data;
  };

  const onModelClick = (type, obj) => {
    if (props.enableEdit) {
      toast.error('You have unsaved changes. Please save before proceeding.');
    } else {
      if (type === 'assignTo') {
        setModalInfo({ enable: true, title: 'Assign To', type });
        fetchNextRespondents(obj);
      } else {
        onAssignToEnd(null);
      }
    }
  };

  const fetchNextRespondents = (obj) => {
    let payload = {
      workflowId: props.data.chartData.workflowId,
      currentTaskId: blockState?.workflow?.id,
      appId: props.match.params.appid,
      orgId: props.user?.orgId,
      dataUniqueId: props.data.chartUniqueId + '_Obligations_Next_Respondent',
      data: {
        docId: props.match.params.id,
        dmId: props.data.chartData.selectedDataModel,
        nextTaskId: obj.id,
      },
    };
    setCurrentButtonClicked(obj);
    props.getNextRespondent(payload);
  };

  const onCloseModel = () => setModalInfo({ enable: false });

  const onAssignToEnd = (v) => {
    let value = [v];
    let payload = {
      isMulti: false,
      workflowId: props.data.chartData.workflowId,
      currentTaskId: blockState?.workflow?.id,
      appId: props.match.params.appid,
      orgId: props.user?.orgId,
      dataUniqueId: props.data.chartUniqueId + '_execute_obligation_End_Workflow',
      data: {
        docId: props.match.params.id,
        dmId: props.data.chartData.selectedDataModel,
        nextTaskId: currentButtonClicked.id,
      },
    };
    if (value[0] !== null && value?.length && value[0]?.value) {
      payload.data['nextRespondent'] = { _id: value[0].value, name: value[0].label };
    }
    props.clearReduxDataOfCurrentComponent({
      key: 'document',
      subKey: [`${props.data.chartUniqueId}_getsingleobligation`],
    });
    props.executeWorkFlow(payload);
  };

  const onAssignTo = (v) => {
    let value = [v];
    let payload = {
      isMulti: false,
      workflowId: props.data.chartData.workflowId,
      currentTaskId: blockState?.workflow?.id,
      appId: props.match.params.appid,
      orgId: props.user?.orgId,
      dataUniqueId: props.data.chartUniqueId + '_execute_obligation_Workflow',
      data: {
        docId: props.match.params.id,
        dmId: props.data.chartData.selectedDataModel,
        nextTaskId: currentButtonClicked.id,
      },
    };
    if (value[0] !== null && value?.length && value[0]?.value) {
      payload.data['nextRespondent'] = { _id: value[0].value, name: value[0].label };
    }
    props.clearReduxDataOfCurrentComponent({
      key: 'document',
      subKey: [`${props.data.chartUniqueId}_getsingleobligation`],
    });
    props.executeWorkFlow(payload);
  };

  const clearCurrentChartReduxStore = () => {
    let subKeys = [
      props.data.chartUniqueId + '_completed_obligation',
      props.data.chartUniqueId + '_execute_obligation_Workflow',
      props.data.chartUniqueId + '_execute_obligation_End_Workflow',
      props.data.chartUniqueId + '_Obligations_Next_Respondent',
      props.data.chartUniqueId + '_obligation_executeWorkflow_reject',
      props.data.chartUniqueId + '_Obligations_Step_Assigning_Upated',
      props.data.chartUniqueId + '_Current_Steps_Members',
      props.data.chartUniqueId + '_getsingleobligation',
    ];
    props.clearReduxDataOfCurrentComponent({ key: 'document', subKey: subKeys });
  };

  const showConfirmationDialogORToast = (type, message) => {
    if (['success', 'rejected', 'completed'].includes(type)) {
      setModalInfo({ enable: true, title: type, type, message });
    }
  };

  const onSucessClick = () => {
    setModalInfo({ enable: false });
    clearCurrentChartReduxStore();
    props.fetchSingleObligation();
  };

  const handleReject = () => {
    if (props.enableEdit) {
      toast.error('You have unsaved changes. Please save before proceeding.');
    } else {
      let payload = {
        workflowId: props.data.chartData.workflowId,
        currentTaskId: blockState?.workflow?.id,
        appId: props.match.params.appid,
        dataUniqueId: props.data.chartUniqueId + '_obligation_executeWorkflow_reject',
        data: {
          docId: props.match.params.id,
          dmId: props.data.chartData.selectedDataModel,
        },
      };
      props.clearReduxDataOfCurrentComponent({
        key: 'document',
        subKey: [`${props.data.chartUniqueId}_getsingleobligation`],
      });
      props.executeWorkFlowReject(payload);
    }
  };

  const onRejectedModel = () => {
    setModalInfo({ enable: false });
    clearCurrentChartReduxStore();
    props.fetchSingleObligation();
  };

  const showHideWorkFlow = (obj) => setAllTasksCompleted(obj);

  useEffect(() => {
    if (props.formData?.document?.[`${props.data.chartUniqueId}_Current_Steps_Members`]) {
      let d = props.formData?.document?.[`${props.data.chartUniqueId}_Current_Steps_Members`];

      setFirstStepsMembersDropdown(d);

      const formattedWorkflow =
        d
          ?.filter((item) => item.name !== blockState?.[selectedBlockName]?.name)
          .map((item) => ({ value: item.name, label: item.name })) || [];

      setDefaultOptionsDropdown(formattedWorkflow);
    }
  }, [
    props.formData?.document?.[`${props.data.chartUniqueId}_Current_Steps_Members`],
    props.members?.name,
    blockState,
    selectedBlockName,
    props.data.chartUniqueId,
  ]);

  const currentStepMembersForobligation = (obj) => {
    let payload = {
      dataUniqueId: `${props.data.chartUniqueId}_Current_Steps_Members`,
      currentStepsId: blockState['workflow'].id,
      payload: { workFlowId: props.data.chartData.workflowId },
      appId: props.match.params.appid,
    };
    setSelectedBlockName(obj);
    props.currentStepMembersForobligation(payload);
  };

  const changeAssigningObligations = (type, block) => {
    currentStepMembersForobligation(block?.value);
    if (type === 'assignToParent') {
      setModalInfo({ enable: true, title: 'Assign To', type });
    }
  };

  const changeAssignee = (payload) => {
    let finalpayload = {
      ...payload,
      appid: props.match.params.appid,
      dataModelId: props.data.chartData.selectedDataModel,
      dataUniqueId: props.data?.chartUniqueId + '_Obligations_Step_Assigning_Upated',
    };
    props.clearReduxDataOfCurrentComponent({
      key: 'document',
      subKey: [`${props.data.chartUniqueId}_getsingleobligation`],
    });
    props.changeAssigneeRequest(finalpayload);
  };

  useEffect(() => {
    if (props.formData?.document?.[`${props.data?.chartUniqueId}_Obligations_Step_Assigning_Upated`]) {
      toast.success(props.formData?.document?.[`${props.data?.chartUniqueId}_Obligations_Step_Assigning_Upated`]?.message);
      onCloseModel();
      clearCurrentChartReduxStore();
      props.fetchSingleObligation();
    }
  }, [props.formData?.document?.[`${props.data?.chartUniqueId}_Obligations_Step_Assigning_Upated`]]);

  const onAssignToParent = (v) => {
    changeAssignee({
      selfAssign: false,
      documentId: props.match.params.id,
      member: (() => {
        const found = firstStepsMembersDropdown.find((item) => item.name === v.value || item.name === v.value);
        return found;
      })(),
    });
  };

  const asignedToMeStep = () => {
    changeAssignee({
      selfAssign: false,
      documentId: props.match.params.id,
      member: { id: props.members._id, name: props.members.name },
    });
  };

  const checkConditions = (conditions, blockState) => {
    if (!conditions || conditions.length === 0) return true;

    let result = null;

    for (const cond of conditions) {
      const fieldValue = blockState[cond.selectedConditionDataElement];
      if (fieldValue === undefined || fieldValue === null) continue;

      let isMet = false;
      switch (cond.selectedConditionOperator) {
        case 'is_equals':
          isMet = fieldValue === cond.value;
          break;
        case 'is_not_equals':
          isMet = fieldValue !== cond.value;
          break;
        case 'contains':
          isMet = Array.isArray(fieldValue) && fieldValue.includes(cond.value);
          break;
        case 'not_contains':
          isMet = Array.isArray(fieldValue) && !fieldValue.includes(cond.value);
          break;
        default:
          isMet = true;
      }

      if (cond.selectedConditionClause === 'or') {
        result = result === null ? isMet : result || isMet;
      } else if (cond.selectedConditionClause === 'and') {
        result = result === null ? isMet : result && isMet;
      }
    }
    return result === null ? false : result;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const blockTheme = {
    Identification: {
      icon: "/media/svg/defaultIdentificationIcon.svg",
      alt: "Identification Icon",
      bg: "#F7EDFF",
    },
    Implementation: {
      icon: "/media/svg/defaultImplementationIcon.svg",
      alt: "Implementation Icon",
      bg: "#FFF4ED",
    },
    Validation: {
      icon: "/media/svg/defaultValidationIcon.svg",
      alt: "Validation Icon",
      bg: "#EDF1FF",
    },
    Realization: {
      icon: "/media/svg/defaultRealizationIcon.svg",
      alt: "Realization Icon",
      bg: "#EDFFED",
    },
  };

  // Fallback for unknown titles
  const fallback = {
    icon: "/media/svg/defaultIdentificationIcon.svg",
    alt: "Default Icon",
    bg: "#F5F5F5",
  };


  return (
    <React.Fragment>
      {blocks?.length > 0 && enableWorkflow ? (
        (() => {
          let visibleBlocks = [];

          if ((blockState['status'] === 'New' || blockState['status'] === 'Rejected') && !isLinked && enableWorkflow) {
            visibleBlocks = [blocks[0]];
          }
          else if (blockState['status'] === 'Active' && blockState['workflow'] && !isLinked && enableWorkflow) {
            const workflowIndex = blocks.findIndex((b) => b.workflowStepId === blockState['workflow'].id);
            if (workflowIndex !== -1) {
              visibleBlocks = blocks.slice(0, workflowIndex + 1);
            }
          }
          else if (blockState?.workflow && blockState['status'] === blockState?.workflow?.label && isLinked && enableWorkflow) {
            const workflowIndex = blocks.findIndex((b) => b.workflowStepId === blockState['workflow'].id);
            if (workflowIndex !== -1) {
              visibleBlocks = blocks.slice(0, workflowIndex + 1);
            }
          }

          blocks.forEach((block, index) => {
            const isAlreadyIncluded = visibleBlocks.some((b) => b.value === block.value);
            const hasBlockState = !!blockState[block.value];

            if (hasBlockState && !isAlreadyIncluded) {
              visibleBlocks.splice(index, 0, block);
            }
          });

          const hasMatchingRole = props?.members?.roles?.some((memberRole) =>
            props?.obligationWorkflowObject[0]?.respondent?.role?.some((assigningRole) => assigningRole?._id === memberRole.id),
          );

          return visibleBlocks.map((block, index) => {
            const accordian = accordionSupport[block.name];
            const backgroundColor = block.accordion?.backgroundColor || '#4D9FEC';

            const shouldDisableFields =
              props.enableWorkflow && blockState?.['status'] !== 'Rejected' &&
              !(
                block?.workflowStepId === blockState?.workflow?.id &&
                blockState?.workflow?.currentRespondent?.id === props.members?._id
              );

            const { icon, alt, bg } = blockTheme[block.title] || fallback;
            return (
              <React.Fragment key={`fragment_${block.name}_${index}`}>
                {block.title ? (
                  accordian?.enable ? (
                    <Accordion
                      key={`block_${block.name}_${index}`}
                      elevation={0}
                      className={classes.accordionRoot}
                      // dynamic color per block (kept inline to reflect per-item data)
                      //style={{ backgroundColor: `${backgroundColor}` }}
                      defaultExpanded={accordian.defaultExpanded || false}
                    >
                      <AccordionSummary
                        className={classes.expandedSummary}
                        expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                      // dynamic summary bg per block config
                      // style={{ backgroundColor: '#FAFAFA' }}
                      >
                        <div className={classes.summaryContainer}>
                          <div className={classes.summaryLeft}>
                            {/* {
                              (() => {
                                const iconMap = {
                                  Identification: { src: '/media/svg/defaultIdentificationIcon.svg', alt: 'Identification Icon' },
                                  Implementation: { src: '/media/svg/defaultImplementationIcon.svg', alt: 'Implementation Icon' },
                                  Validation: { src: '/media/svg/defaultValidationIcon.svg', alt: 'Validation Icon' },
                                  Realization: { src: '/media/svg/defaultRealizationIcon.svg', alt: 'Realization Icon' },
                                };
                                const { src, alt } = iconMap[block.title] || {};
                                return src ? <img src={src} alt={alt} className={classes.headerIcon} /> : null;
                              })()
                            } */}
                            {/* <div style={{ height: '55px', width: '55px', backgroundColor: '#F7EDFF', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '19.5px 19px 18.5px 19px', boxSizing: 'border-box' }}>
                              <img src={'/media/svg/defaultIdentificationIcon.svg'} alt="defaultIcon" style={{ width: '28px', height: '28px' }} />
                            </div> */}
                            <div
                              style={{
                                height: 55,
                                width: 55,
                                backgroundColor: bg,
                                borderRadius: 4,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "19.5px 19px 18.5px 19px",
                                boxSizing: "border-box",
                              }}
                            >
                              <img src={icon} alt={alt} style={{ width: 28, height: 28 }} />
                            </div>
                            {/* {(() => {
                              const iconMap = {
                                Identification: { src: '/media/svg/seoImage.svg', alt: 'Identification Icon' },
                                Implementation: { src: '/media/svg/implementationNewIcon.svg', alt: 'Implementation Icon' },
                                Validation: { src: '/media/svg/validationNewIcon.svg', alt: 'Validation Icon' },
                                Realization: { src: '/media/svg/relazationNewIcon.svg', alt: 'Realization Icon' },
                              };
                              const { src, alt } = iconMap[block.title] || {};
                              return src ? <img src={src} alt={alt} className={classes.headerIcon} /> : null;
                            })()} */}
                          </div>
                          <div className={classes.summaryRight}>
                            <Typography className={classes.title}>{block.title}</Typography>
                            <Typography className={classes.subtitle}>
                              Capture the opportunity that might result in cost recovery.
                            </Typography>
                          </div>
                        </div>
                      </AccordionSummary>

                      <AccordionDetails className={`${classes.accordionDetails} myScrollContainer`}>
                        <div className={classes.detailsWrapper}>
                          {index === 0 &&
                            !blockState['workflow'] && !isLinked &&
                            blockState['documentStatus'] === 'New' &&
                            hasMatchingRole && (
                              <div className={classes.flexRowEnd12}>
                                <Button className={classes.btnReject} onClick={() => onRejectObligation()}>
                                  Reject
                                </Button>
                                <Button className={classes.btnAccept} onClick={() => onAcceptObligation()}>
                                  Accept
                                </Button>
                              </div>
                            )}

                          {index === 0 && !blockState['workflow'] && blockState['documentStatus'] === 'Rejected' && (
                            <div className={classes.flexRowStart32}>
                              <div className={classes.flexRowStart8}>
                                <Typography className={classes.label}>Action Owner</Typography>
                                <Typography className={classes.label}>:</Typography>
                                <div className={classes.flexRowStart8}>
                                  <AccountCircleIcon className={classes.greenIcon} />
                                  <Typography className={classes.value}>{blockState['rejectedBy']?.name}</Typography>
                                </div>
                              </div>
                              <div className={classes.flexRowStart8}>
                                <Typography className={classes.label}>Action Date</Typography>
                                <Typography className={classes.label}>:</Typography>
                                <div className={classes.flexRowStart8}>
                                  <img
                                    src={'/media/svg/calendarObligations.svg'}
                                    alt="calendar Obligations"
                                    className={classes.calendarIcon}
                                  />
                                  <Typography className={classes.value}>
                                    {new Date(blockState?.rejectedDate).toISOString().split('T')[0]}
                                  </Typography>
                                </div>
                              </div>
                            </div>
                          )}

                          {blockState['workflow'] &&
                            blockState['workflow']?.currentRespondent &&
                            blockState['workflow']?.initDate && (
                              <div className={classes.flexRowStart32}>
                                <div className={classes.flexRowStart8}>
                                  <Typography className={classes.label}>Action Owner</Typography>
                                  <Typography className={classes.label}>:</Typography>
                                  <div className={classes.flexRowStart8}>
                                    <Avatar className={classes.commentAvatar}>
                                      {getInitials(blockState[block.value]?.name)}
                                    </Avatar>
                                    <Typography className={classes.value}>{blockState[block.value]?.name}</Typography>
                                  </div>

                                  {!shouldDisableFields && blockState?.['status'] !== 'Rejected' && (
                                    <div
                                      className={clsx(classes.flexRowStart4Clickable, classes.linkSm)}
                                      onClick={() => {
                                        changeAssigningObligations('assignToParent', block);
                                      }}
                                    >
                                      <Typography className={classes.linkSm}>Edit</Typography>
                                      <CreateIcon style={{ height: 14, width: 14 }} />
                                    </div>
                                  )}

                                  {hasMatchingRole &&
                                    blockState[block.value]?.name !== null &&
                                    blockState[block.value]?.name !== props?.members?.name && (
                                      <Tooltip title="Self Assign" arrow>
                                        <div
                                          className={clsx(classes.flexRowStart4Clickable, classes.linkSm)}
                                          onClick={() => asignedToMeStep()}
                                        >
                                          <Typography className={classes.linkSm}>
                                            Self Assign{' '}
                                            <img
                                              src={'/media/svg/selfAssign.svg'}
                                              alt="Self Assign"
                                              width="14"
                                              height="14"
                                              style={{ verticalAlign: 'text-bottom' }}
                                            />
                                          </Typography>
                                        </div>
                                      </Tooltip>
                                    )}
                                </div>

                                <div className={classes.flexRowStart8}>
                                  <Typography className={classes.label}>Action Date</Typography>
                                  <Typography className={classes.label}>:</Typography>
                                  <div className={classes.flexRowStart8}>
                                    <img
                                      src={'/media/svg/calendarObligations.svg'}
                                      alt="calendar Obligations"
                                      className={classes.calendarIcon}
                                    />
                                    <Typography className={classes.value}>
                                      {(() => {
                                        const raw = blockState?.workflow?.initDate;
                                        const dateValue = raw?.$date ?? raw;
                                        const d = dateValue ? new Date(dateValue) : null;
                                        return d && !Number.isNaN(d.getTime())
                                          ? d.toISOString().split("T")[0]
                                          : "N/A";
                                      })()}
                                    </Typography>
                                  </div>
                                </div>
                              </div>
                            )}


                          {/* {blockState['workflow'] &&
                            blockState['workflow']?.currentRespondent &&
                            blockState['workflow']?.initDate && (
                           
                            )} */}

                          {/* {
                            props.match.params.id && (
                              <div className={classes.flexRowStart32}>
                                {
                                  !blockState['workflow'] && blockState['createdByName'] && blockState['status'] !== 'Rejected' &&
                                  (
                                    <div className={classes.flexRowStart8}>
                                      <Typography className={classes.label}>Action Owner</Typography>
                                      <Typography className={classes.label}>:</Typography>
                                      <div className={classes.flexRowStart8}>
                                        <Avatar className={classes.commentAvatar}>
                                          {getInitials(blockState['createdByName'])}
                                        </Avatar>
                                        <Typography className={classes.value}>{blockState['createdByName']}</Typography>
                                      </div>
                                    </div>
                                  )
                                }

                                {
                                  !blockState['workflow'] && blockState['createdByName'] && blockstate['createdDate'] && blockState['status'] !== 'Rejected' && (
                                    <div className={classes.flexRowStart8}>
                                      <Typography className={classes.label}>Action Date</Typography>
                                      <Typography className={classes.label}>:</Typography>
                                      <div className={classes.flexRowStart8}>
                                        <img
                                          src={'/media/svg/calendarObligations.svg'}
                                          alt="calendar Obligations"
                                          className={classes.calendarIcon}
                                        />
                                        <Typography className={classes.value}>
                                          {new Date(blockState?.createdDate).toISOString().split('T')[0]}
                                        </Typography>
                                      </div>
                                    </div>
                                  )
                                }

                                {
                                  blockState['workflow'] &&
                                  blockState['workflow']?.currentRespondent &&
                                  blockState['workflow']?.initDate
                                  && (
                                    <div className={classes.flexRowStart8}>
                                      <Typography className={classes.label}>Action Owner</Typography>
                                      <Typography className={classes.label}>:</Typography>
                                      <div className={classes.flexRowStart8}>
                                        <Avatar className={classes.commentAvatar}>
                                          {getInitials(blockState[block.value]?.name)}
                                        </Avatar>
                                        <Typography className={classes.value}>{blockState[block.value]?.name}</Typography>
                                      </div>

                                      {!shouldDisableFields && blockState?.['status'] !== 'Rejected' && (
                                        <div
                                          className={clsx(classes.flexRowStart4Clickable, classes.linkSm)}
                                          onClick={() => {
                                            changeAssigningObligations('assignToParent', block);
                                          }}
                                        >
                                          <Typography className={classes.linkSm}>Edit</Typography>
                                          <CreateIcon style={{ height: 14, width: 14 }} />
                                        </div>
                                      )}

                                      {hasMatchingRole &&
                                        blockState[block.value]?.name !== null &&
                                        blockState[block.value]?.name !== props?.members?.name && (
                                          <Tooltip title="Self Assign" arrow>
                                            <div
                                              className={clsx(classes.flexRowStart4Clickable, classes.linkSm)}
                                              onClick={() => asignedToMeStep()}
                                            >
                                              <Typography className={classes.linkSm}>
                                                Self Assign{' '}
                                                <img
                                                  src={'/media/svg/selfAssign.svg'}
                                                  alt="Self Assign"
                                                  width="14"
                                                  height="14"
                                                  style={{ verticalAlign: 'text-bottom' }}
                                                />
                                              </Typography>
                                            </div>
                                          </Tooltip>
                                        )}
                                    </div>
                                  )
                                }

                                {
                                  blockState['workflow'] &&
                                  blockState['workflow']?.currentRespondent &&
                                  blockState['workflow']?.initDate
                                  && (
                                    <div className={classes.flexRowStart8}>
                                      <Typography className={classes.label}>Action Date</Typography>
                                      <Typography className={classes.label}>:</Typography>
                                      <div className={classes.flexRowStart8}>
                                        <img
                                          src={'/media/svg/calendarObligations.svg'}
                                          alt="calendar Obligations"
                                          className={classes.calendarIcon}
                                        />
                                        <Typography className={classes.value}>
                                          {new Date(blockState?.workflow?.initDate).toISOString().split('T')[0]}
                                        </Typography>
                                      </div>
                                    </div>
                                  )
                                }

                              </div>
                            )
                          } */}


                          {/* <div style={{ marginTop: '12px', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ height: '130px', boxSizing: 'border-box', padding: '20px', backgroundColor: '#F9FBFF', borderRadius: '4px', border: '1px solid #416DDD', display: 'flex', flexDirection: 'row', gap: '8px' }}>
                              <div style={{ height: '36px', width: '20px', padding: '8px, 0px, 8px, 8px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img src={'/media/svg/iconcontainer.svg'} alt="Contract Details" width="30px" height="30px" />
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Typography style={{ fontSize: '14px', fontWeight: '400', color: '#416DDD', fontStyle: 'normal', lineHeight: '21px' }}>Summary</Typography>
                                <Typography style={{ fontSize: '14px', fontWeight: '400', color: '#2B2C35', fontStyle: 'normal', lineHeight: '21px' }}>Supplier invoices show that goods were delivered late (beyond PO delivery date). Contract clause states that in such cases, Buyer is entitled to a 2% performance discount. Invoices were still paid on original terms without discount → missed savings opportunity of £48,000.</Typography>
                              </div>
                            </div>
                            <div style={{ height: '130px', boxSizing: 'border-box', padding: '20px', backgroundColor: '#FCFAFF', borderRadius: '4px', border: '1px solid #522BAE', display: 'flex', flexDirection: 'row', gap: '8px' }}>
                              <div style={{ height: '36px', width: '20px', padding: '8px, 0px, 8px, 8px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img src={'/media/svg/iconcontainer.svg'} alt="Contract Details" width="30px" height="30px" />
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Typography style={{ fontSize: '14px', fontWeight: '400', color: '#522BAE', fontStyle: 'normal', lineHeight: '21px' }}>Explainability</Typography>
                                <Typography style={{ fontSize: '14px', fontWeight: '400', color: '#2B2C35', fontStyle: 'normal', lineHeight: '21px' }}>Supplier invoices show that goods were delivered late (beyond PO delivery date). Contract clause states that in such cases, Buyer is entitled to a 2% performance discount. Invoices were still paid on original terms without discount → missed savings opportunity of £48,000.</Typography>
                              </div>
                            </div>
                            <div style={{ height: '130px', boxSizing: 'border-box', padding: '20px', backgroundColor: '#FAFFFC', borderRadius: '4px', border: '1px solid #19934C', display: 'flex', flexDirection: 'row', gap: '8px' }}>
                              <div style={{ height: '36px', width: '20px', padding: '8px, 0px, 8px, 8px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img src={'/media/svg/iconcontainer.svg'} alt="Contract Details" width="30px" height="30px" />
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Typography style={{ fontSize: '14px', fontWeight: '400', color: '#19934C', fontStyle: 'normal', lineHeight: '21px' }}>Obligation Language </Typography>
                                <Typography style={{ fontSize: '14px', fontWeight: '400', color: '#2B2C35', fontStyle: 'normal', lineHeight: '21px' }}>Supplier invoices show that goods were delivered late (beyond PO delivery date). Contract clause states that in such cases, Buyer is entitled to a 2% performance discount. Invoices were still paid on original terms without discount → missed savings opportunity of £48,000.</Typography>
                              </div>
                            </div>
                          </div> */}
                          {block.subBlock
                            ? block.subBlock.map((res, subIdx) =>
                              checkConditions(res.conditions, blockState) ? (
                                <div className={classes.blockWrapper} key={`${block.name}_${index}_sub_${subIdx}`}>
                                  <RenderComponentBasedOnBlock
                                    shouldDisableFields={shouldDisableFields}
                                    isSubBlock={true}
                                    key={`${block.name}_${index}`}
                                    {...props}
                                    block={res}
                                    showHideWorkFlow={showHideWorkFlow}
                                  />
                                </div>
                              ) : null,
                            )
                            : (
                              <div className={classes.blockWrapper}>
                                <RenderComponentBasedOnBlock
                                  shouldDisableFields={shouldDisableFields}
                                  key={`${block.name}_${index}`}
                                  {...props}
                                  isSubBlock={false}
                                  block={block}
                                  showHideWorkFlow={showHideWorkFlow}
                                />
                              </div>
                            )}

                          {props.isCommentSectionEnable &&
                            block.isCommentSectionEnable &&
                            blockState?.['documentStatus'] === 'Active' && (
                              <CommentViewer {...props} block={block} shouldDisableFields={shouldDisableFields} index={index} />
                            )}

                          {props.enableWorkflow &&
                            !shouldDisableFields &&
                            Boolean(Object.keys(props?.nextStep || {}).length) &&
                            Array.isArray(props?.nextStep?.currentPossibleAction) &&
                            props?.nextStep?.currentPossibleAction.map((cpa, cpaIdx) => {
                              const disableMoveButton = cpa?.label === 'Realization' && !allTasksCompleted;
                              return (
                                <div className={classes.flexRowEnd12} key={`cpa_${cpaIdx}`}>

                                  {
                                    props?.match?.params?.id && blockState["workflow"] && blockState?.["status"] !== "Rejected" && <Button
                                      variant="contained"
                                      className={classes.btnMove}
                                      style={{ backgroundColor: 'red', color: 'white', border: 'none' }}
                                      onClick={() => { onRejectObligation() }}
                                    >
                                      Reject This Document
                                    </Button>
                                  }

                                  {props.enableEdit && blockState?.['status'] !== 'Rejected' ? (
                                    <Button className={classes.btnSave} onClick={props.handleSave}>
                                      Save
                                    </Button>
                                  ) : null}

                                  {props.enableRejectButton &&
                                    blockState?.['status'] !== 'Rejected' &&
                                    props.CurrentObligationWorkflow?.type !== 'start' &&
                                    props.CurrentObligationWorkflow?.type !== 'end' ? (
                                    <Button className={classes.btnMove} onClick={handleReject} style={{ backgroundColor: 'rgba(239, 108, 0, 1)' }}>
                                      Reject
                                    </Button>
                                  ) : null}

                                  {Object.keys(cpa || {}).length > 0 && blockState?.['status'] !== 'Rejected' && (
                                    <>
                                      {cpa?.type === 'end' ? (
                                        <Button
                                          className={clsx(classes.btnMove, disableMoveButton && classes.btnMoveDisabled)}
                                          onClick={() => onModelClick('endAction', cpa)}
                                          disabled={disableMoveButton}
                                        >
                                          {cpa?.label}
                                        </Button>
                                      ) : (
                                        <Button
                                          className={clsx(classes.btnMove, disableMoveButton && classes.btnMoveDisabled)}
                                          onClick={() => onModelClick('assignTo', cpa)}
                                          disabled={disableMoveButton}
                                        >
                                          {`Move to ${cpa?.label}`}
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  ) : null
                ) : null}
              </React.Fragment>
            );
          });
        })()
      ) : blocks?.length > 0 && !enableWorkflow ? (
        (() => {
          let visibleBlocks = [];

          visibleBlocks = blocks;

          return visibleBlocks.map((block, index) => {
            const accordian = accordionSupport[block.name];
            const backgroundColor = block.accordion?.backgroundColor || '#4D9FEC';

            const shouldDisableFields = false;


            return (
              <React.Fragment key={`fragment_${block.name}_${index}`}>
                {block.title ? (
                  accordian?.enable ? (
                    <Accordion
                      key={`block_${block.name}_${index}`}
                      elevation={0}
                      className={classes.accordionRoot}
                      // dynamic color per block (kept inline to reflect per-item data)
                      //style={{ backgroundColor: `${backgroundColor}` }}
                      defaultExpanded={accordian.defaultExpanded || false}
                    >
                      <AccordionSummary
                        className={classes.expandedSummary}
                        expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                        // dynamic summary bg per block config
                        style={{ backgroundColor: '#FAFAFA' }}
                      >
                        <div className={classes.summaryContainer}>
                          <div className={classes.summaryLeft}>
                            {/* <img src={'/media/svg/IndentificationUsers.svg'} alt={'hello'} className={classes.headerIcon} /> */}

                            {(() => {
                              const iconMap = {
                                Identification: { src: '/media/svg/seoImage.svg', alt: 'Identification Icon' },
                                Implementation: { src: '/media/svg/ImplementationIcon.svg', alt: 'Implementation Icon' },
                                Validation: { src: '/media/svg/ValidationIcon.svg', alt: 'Validation Icon' },
                                Realization: { src: '/media/svg/RealizationIcon.svg', alt: 'Realization Icon' },
                              };
                              const { src, alt } = iconMap[block.title] || {};
                              return src ? <img src={src} alt={alt} className={classes.headerIcon} /> : null;
                            })()}
                          </div>
                          <div className={classes.summaryRight}>
                            <Typography className={classes.title}>{block.title}</Typography>
                            <Typography className={classes.subtitle}>
                              Capture the opportunity that might result in cost recovery.
                            </Typography>
                          </div>
                        </div>
                      </AccordionSummary>

                      <AccordionDetails className={`${classes.accordionDetails} myScrollContainer`}>
                        <div className={classes.detailsWrapper}>
                          {block.subBlock
                            ? block.subBlock.map((res, subIdx) =>
                              checkConditions(res.conditions, blockState) ? (
                                <div className={classes.blockWrapper} key={`${block.name}_${index}_sub_${subIdx}`}>
                                  <RenderComponentBasedOnBlock
                                    shouldDisableFields={shouldDisableFields}
                                    key={`${block.name}_${index}`}
                                    {...props}
                                    block={res}
                                    showHideWorkFlow={showHideWorkFlow}
                                  />
                                </div>
                              ) : null,
                            )
                            : (
                              <div className={classes.blockWrapper}>
                                <RenderComponentBasedOnBlock
                                  shouldDisableFields={shouldDisableFields}
                                  key={`${block.name}_${index}`}
                                  {...props}
                                  block={block}
                                  showHideWorkFlow={showHideWorkFlow}
                                />
                              </div>
                            )}

                          <div className={classes.flexRowEnd12} >
                            {props.enableEdit ? (
                              <Button className={classes.btnSave} onClick={props.handleSave}>
                                Save
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  ) : null
                ) : null}
              </React.Fragment>
            );
          });
        })()
      ) :
        (
          <div>
            <Typography>No Blocks configured to display.</Typography>
          </div>
        )}

      {modalInfo?.enable && (
        <WorkFlowModelViewer
          {...props}
          allValues={getNextRespondents()}
          modalInfo={modalInfo}
          onSucessClick={onSucessClick}
          onCloseModel={onCloseModel}
          onAssignTo={onAssignTo}
          onRejectedModel={onRejectedModel}
          defaultOptionsDropdown={defaultOptionsDropdown}
          onAssignToParent={onAssignToParent}
        />
      )}
    </React.Fragment>
  );
};

export default StepLevelBlocksViewer;
